import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
    ResponsiveModalTrigger,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Calendar } from '@/components/ui/shadcn/calendar';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { Switch } from '@/components/ui/shadcn/switch';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import { businessNow, businessTimeNow, businessToday, isAfterBusinessToday } from '@/lib/business-time';
import { cn } from '@/lib/utils';
import type { AddMeetingModalProps, MeetingFormData } from '@/types/forms/companies';
import { useForm } from '@inertiajs/react';
import { format } from 'date-fns';
import { Building2, Calendar as CalendarIcon, Clock, Users } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export default function AddMeetingModal({
    company,
    people = [],
    trigger,
    meeting,
    defaultParticipantIds,
    open: controlledOpen,
    onOpenChange,
}: AddMeetingModalProps) {
    const { t } = useTranslations();
    const isEdit = Boolean(meeting);
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (value: boolean) => {
        if (isControlled) {
            onOpenChange?.(value);
        } else {
            setInternalOpen(value);
        }
    };
    const [date, setDate] = useState<Date>(businessNow());
    const [time, setTime] = useState(() => businessTimeNow());

    const { data, setData, post, put, processing, errors, reset } = useForm<MeetingFormData>({
        description: '',
        meeting_date: businessToday(),
        meeting_time: businessTimeNow(),
        meeting_format: 'in_person',
        location: '',
        participants: [],
        create_follow_up: false,
        follow_up_date: '',
        follow_up_note: '',
    });

    const peopleOptions = useMemo(
        () =>
            people.map((person) => ({
                value: person.id.toString(),
                label: `${person.first_name} ${person.last_name}`,
            })),
        [people],
    );

    const descriptionLength = data.description?.length ?? 0;

    // In edit mode, populate the form from the meeting whenever the dialog opens.
    useEffect(() => {
        if (!open || !meeting) {
            return;
        }

        const meetingTime = (meeting.meeting_time ?? businessTimeNow()).slice(0, 5);
        const parsedDate = meeting.meeting_date ? new Date(meeting.meeting_date) : businessNow();

        setData({
            description: meeting.description ?? '',
            meeting_date: meeting.meeting_date,
            meeting_time: meetingTime,
            meeting_format: meeting.meeting_format ?? 'in_person',
            location: meeting.location ?? '',
            participants: meeting.participantIds,
            create_follow_up: Boolean(meeting.follow_up_date || meeting.follow_up_note),
            follow_up_date: meeting.follow_up_date ?? '',
            follow_up_note: meeting.follow_up_note ?? '',
        });
        if (!Number.isNaN(parsedDate.getTime())) {
            setDate(parsedDate);
        }
        setTime(meetingTime);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, meeting]);

    // In create mode, pre-select the contact(s) whose page opened the modal.
    useEffect(() => {
        if (!open || meeting || !defaultParticipantIds?.length) {
            return;
        }
        setData('participants', defaultParticipantIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, meeting]);

    const handleDateSelect = (selectedDate: Date | undefined) => {
        if (!selectedDate) {
            return;
        }

        setDate(selectedDate);
        setData('meeting_date', format(selectedDate, 'yyyy-MM-dd'));
    };

    const handleTimeChange = (timeValue: string) => {
        setTime(timeValue);
        setData('meeting_time', timeValue);
    };

    const submit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!company) return;

        if (isEdit && meeting) {
            put(route('company.meeting.update', [company.id, meeting.id]), {
                preserveScroll: true,
                onSuccess: () => setOpen(false),
            });

            return;
        }

        post(route('company.meeting.store', company.id), {
            onSuccess: () => {
                setOpen(false);
                reset();
                setDate(businessNow());
                setTime(businessTimeNow());
            },
        });
    };

    const handleOpenChange = (isOpen: boolean) => {
        setOpen(isOpen);
        if (!isOpen) {
            reset();
            setDate(businessNow());
            setTime(businessTimeNow());
            setData('meeting_date', businessToday());
            setData('meeting_time', businessTimeNow());
            setData('meeting_format', 'in_person');
            setData('create_follow_up', false);
            setData('follow_up_date', '');
            setData('follow_up_note', '');
        }
    };

    return (
        <ResponsiveModal open={open} onOpenChange={handleOpenChange} variant="sheet">
            {trigger ? (
                <ResponsiveModalTrigger asChild>{trigger}</ResponsiveModalTrigger>
            ) : !isControlled ? (
                <ResponsiveModalTrigger asChild>
                    <Button variant="outline" size="sm">
                        <Users className="mr-2 h-4 w-4" />
                        {t('companies.forms.add_meeting.buttons.add_meeting', 'Pridėti susitikimą')}
                    </Button>
                </ResponsiveModalTrigger>
            ) : null}
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle>
                        {isEdit
                            ? t('companies.forms.add_meeting.edit_title', 'Redaguoti susitikimą')
                            : t('companies.forms.add_meeting.title', 'Pridėti susitikimą')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {t('companies.forms.add_meeting.description', 'Užregistruokite įvykusį susitikimą su kliento kontaktu.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
                    <ResponsiveModalBody className="space-y-4">
                        <div className="bg-muted/30 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm">
                            <Building2 className="h-4 w-4" />
                            <span>
                                {t('companies.forms.add_meeting.labels.company_context', 'Įmonė: :name').replace(':name', company?.name || '—')}
                            </span>
                        </div>

                        <div className="space-y-2">
                            <Label>
                                {t('companies.forms.add_meeting.labels.participants', 'Kontaktinis asmuo')}{' '}
                                <span className="text-destructive">*</span>
                            </Label>
                            <MultiSelect
                                options={peopleOptions}
                                selected={data.participants}
                                onChange={(selected) => setData('participants', selected)}
                                placeholder={t('companies.forms.add_meeting.placeholders.select_participants', 'Pasirinkite kontaktą...')}
                                searchPlaceholder={t('companies.forms.add_meeting.placeholders.search_people', 'Ieškoti kontaktų...')}
                                emptyText={t('companies.forms.add_meeting.placeholders.no_people', 'Kontaktų nerasta.')}
                                className={errors.participants ? 'border-destructive' : ''}
                            />
                            {errors.participants && <p className="text-destructive text-sm">{errors.participants}</p>}
                        </div>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="space-y-2">
                                <Label>
                                    {t('companies.forms.add_meeting.labels.meeting_date', 'Susitikimo data')}{' '}
                                    <span className="text-destructive">*</span>
                                </Label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            className={cn(
                                                'w-full justify-start text-left font-normal',
                                                !date && 'text-muted-foreground',
                                                errors.meeting_date && 'border-destructive',
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {date
                                                ? format(date, 'yyyy-MM-dd')
                                                : t('companies.forms.add_meeting.placeholders.pick_date', 'Pasirinkite datą')}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0">
                                        <Calendar
                                            mode="single"
                                            selected={date}
                                            onSelect={handleDateSelect}
                                            disabled={isAfterBusinessToday}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                                {errors.meeting_date && <p className="text-destructive text-sm">{errors.meeting_date}</p>}
                            </div>

                            <div className="min-w-0 space-y-2">
                                <Label htmlFor="meeting_time">
                                    {t('companies.forms.add_meeting.labels.meeting_time', 'Susitikimo laikas')}{' '}
                                    <span className="text-destructive">*</span>
                                </Label>
                                <div className="relative">
                                    <Clock className="text-muted-foreground pointer-events-none absolute top-2.5 left-3 h-4 w-4" />
                                    <Input
                                        id="meeting_time"
                                        type="time"
                                        required
                                        value={time}
                                        onChange={(e) => handleTimeChange(e.target.value)}
                                        className={cn('pl-9', errors.meeting_time ? 'border-destructive' : '')}
                                    />
                                </div>
                                {errors.meeting_time && <p className="text-destructive text-sm">{errors.meeting_time}</p>}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>{t('companies.forms.add_meeting.labels.meeting_format', 'Susitikimo tipas')}</Label>
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                {[
                                    { value: 'in_person', label: t('companies.forms.add_meeting.options.in_person', 'Gyvai') },
                                    { value: 'online', label: t('companies.forms.add_meeting.options.online', 'Online') },
                                    {
                                        value: 'customer_visited',
                                        label: t('companies.forms.add_meeting.options.customer_visited', 'Klientas atvyko'),
                                    },
                                ].map((option) => (
                                    <Button
                                        key={option.value}
                                        type="button"
                                        variant={data.meeting_format === option.value ? 'default' : 'outline'}
                                        onClick={() => setData('meeting_format', option.value)}
                                        className="justify-center"
                                    >
                                        {option.label}
                                    </Button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="meeting_location">{t('companies.forms.add_meeting.labels.location', 'Vieta')}</Label>
                            <Input
                                id="meeting_location"
                                value={data.location || ''}
                                onChange={(e) => setData('location', e.target.value)}
                                placeholder={t('companies.forms.add_meeting.placeholders.location', 'Susitikimo vieta (nebūtina)')}
                            />
                            {errors.location && <p className="text-destructive text-sm">{errors.location}</p>}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="meeting_description">
                                {t('companies.forms.add_meeting.labels.description', 'Užrašai')} <span className="text-destructive">*</span>
                            </Label>
                            <Textarea
                                id="meeting_description"
                                value={data.description}
                                onChange={(e) => setData('description', e.target.value)}
                                placeholder={t(
                                    'companies.forms.add_meeting.placeholders.meeting_notes',
                                    'Trumpai aprašykite, kas buvo aptarta, kokie poreikiai ar kiti svarbūs aspektai...',
                                )}
                                rows={5}
                                className={errors.description ? 'border-destructive' : ''}
                            />
                            <div className="text-muted-foreground text-right text-xs">{descriptionLength} / 2000</div>
                            {errors.description && <p className="text-destructive text-sm">{errors.description}</p>}
                        </div>

                        <div className="space-y-3 border-t pt-4">
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="meeting-create-follow-up"
                                    checked={data.create_follow_up}
                                    onCheckedChange={(checked) => setData('create_follow_up', checked)}
                                />
                                <Label htmlFor="meeting-create-follow-up" className="text-sm font-medium">
                                    {t('companies.forms.follow_up.labels.create', 'Sukurti tolimesnį veiksmą')}
                                </Label>
                            </div>

                            {data.create_follow_up ? (
                                <div className="bg-muted/20 grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="meeting_follow_up_date">
                                            {t('companies.forms.follow_up.labels.date', 'Tolimesnio veiksmo data')}
                                        </Label>
                                        <DatePicker
                                            value={data.follow_up_date}
                                            onValueChange={(value) => setData('follow_up_date', value)}
                                            placeholder={t('companies.forms.follow_up.placeholders.date', 'Pasirinkite datą')}
                                            contentSide="top"
                                            className={errors.follow_up_date ? 'border-destructive' : ''}
                                        />
                                        {errors.follow_up_date && <p className="text-destructive text-sm">{errors.follow_up_date}</p>}
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="meeting_follow_up_note">{t('companies.forms.follow_up.labels.note', 'Pastaba')}</Label>
                                        <Textarea
                                            id="meeting_follow_up_note"
                                            value={data.follow_up_note}
                                            onChange={(event) => setData('follow_up_note', event.target.value)}
                                            placeholder={t('companies.forms.follow_up.placeholders.note', 'Trumpa pastaba...')}
                                            rows={2}
                                            className={errors.follow_up_note ? 'border-destructive' : ''}
                                        />
                                        {errors.follow_up_note && <p className="text-destructive text-sm">{errors.follow_up_note}</p>}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                            {t('companies.forms.add_meeting.buttons.cancel', 'Atšaukti')}
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing
                                ? t('companies.forms.add_meeting.buttons.saving', 'Išsaugoma...')
                                : isEdit
                                  ? t('companies.forms.add_meeting.buttons.update_meeting', 'Atnaujinti susitikimą')
                                  : t('companies.forms.add_meeting.buttons.save_meeting', 'Išsaugoti susitikimą')}
                        </Button>
                    </ResponsiveModalFooter>
                </form>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
